import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAvailability, useSlots } from "../../application/useScheduler";
import {
    nextDateWithAvailability,
    toDateKey,
    weekdayOfDateKey,
} from "../../domain/formatting";
import {
    OnlinePlatform,
    ScheduleSessionInput,
    ScheduleSubject,
    SchedulerSettings,
    SessionUbication,
    TherapistSpace,
} from "../../domain/types";
import { slotMatchingMinute } from "../../domain/slotSelection";
import { sessionTypeOf, subjectPayload } from "../../domain/subject";
import PatientFinder, { type PatientOption } from "../../../../shared/ui/patient/PatientFinder";
import { Modal } from "../../../../shared/ui/modal";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import type { CenterCourse } from "../../../center";
import MentoringModuleFields from "./MentoringModuleFields";
import SessionTypeSelector from "./SessionTypeSelector";
import SlotGrid from "./SlotGrid";
import { openDatePicker } from "./openDatePicker";

/** A phone or tablet: the primary pointer is a finger. */
function isTouchScreen(): boolean {
    return (
        typeof window !== "undefined" &&
        window.matchMedia?.("(pointer: coarse)").matches === true
    );
}

interface ScheduleSessionModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (payload: ScheduleSessionInput) => void;
    settings: SchedulerSettings;
    spaces: TherapistSpace[];
    patients?: PatientOption[];
    /**
     * The patient or couple this booking is for, when the caller already knows
     * — opened from that subject's page. Given one, the finder is not rendered
     * at all: a picker that cannot pick reads as broken.
     */
    subject?: ScheduleSubject | null;
    initialDate: string;
    /**
     * The time the caller pointed at, as minutes since midnight in the
     * therapist's timezone — the week grid's clicked hour. The matching slot is
     * chosen once the day's slots arrive; a busy hour simply leaves the grid
     * untouched rather than picking some other time on the therapist's behalf.
     */
    initialMinute?: number | null;
    isSaving?: boolean;
    errorMessage?: string | null;
    /** Whether the therapist has adopted AlchelyMeeting; decides whether it is
     *  offered as a platform. The caller owns the query. */
    alchelyMeetingAvailable?: boolean;
    /**
     * Whether "prepare the session now" starts ticked: a training centre's
     * mentor prepares every mentoring, a therapist chooses to.
     */
    openSessionByDefault?: boolean;
    /**
     * A training centre's courses, to book a mentoring on one of their
     * modules. Empty outside a training centre, which hides the fields.
     */
    courses?: CenterCourse[];
}

export default function ScheduleSessionModal({
    open,
    onClose,
    onSubmit,
    settings,
    spaces,
    patients = [],
    subject = null,
    initialDate,
    initialMinute = null,
    isSaving = false,
    errorMessage = null,
    alchelyMeetingAvailable = false,
    openSessionByDefault = false,
    courses = [],
}: ScheduleSessionModalProps) {
    const { t } = useTranslation();
    const [openSession, setOpenSession] = useState(openSessionByDefault);
    // Each opening starts from the default, whatever the last booking chose.
    useEffect(() => {
        if (open) setOpenSession(openSessionByDefault);
    }, [open, openSessionByDefault]);
    const [patientId, setPatientId] = useState("");
    const [date, setDate] = useState(initialDate);
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
    const [ubication, setUbication] = useState(SessionUbication.ONLINE);
    const [platform, setPlatform] = useState<OnlinePlatform | null>(null);
    const [spaceId, setSpaceId] = useState<string | null>(null);
    // The appointment's name. Filled in from whoever it is with until the
    // therapist types their own, which from then on is left alone.
    const [title, setTitle] = useState("");
    const [titleEdited, setTitleEdited] = useState(false);

    // A fixed subject settles who the booking is for; otherwise it is whatever
    // was picked in the finder, whose `kind` decides which field it travels in
    // — and, through the kind of session, how long the offered slots are.
    const chosen = patients.find((option) => option.id === patientId) ?? null;
    const bookingSubject: ScheduleSubject | null =
        subject ??
        (chosen
            ? { kind: chosen.kind ?? "patient", id: chosen.id, label: chosen.name }
            : null);
    const sessionType = sessionTypeOf(bookingSubject?.kind);

    // The course and module the mentoring works on. The course starts as the
    // student's — or the centre's only one — each time who it is for changes;
    // after that it is the mentor's to change.
    const [courseId, setCourseId] = useState("");
    const [moduleId, setModuleId] = useState("");
    const subjectCourseId = chosen?.courseId ?? null;
    const defaultCourseId =
        subjectCourseId && courses.some((course) => course.id === subjectCourseId)
            ? subjectCourseId
            : courses.length === 1
              ? courses[0].id
              : "";
    useEffect(() => {
        if (!open) return;
        setCourseId(defaultCourseId);
        setModuleId("");
    }, [open, defaultCourseId, bookingSubject?.id]);

    const slotsQuery = useSlots(open ? date : "", sessionType);
    const availabilityQuery = useAvailability();

    const windows = useMemo(
        () => availabilityQuery.data ?? [],
        [availabilityQuery.data]
    );

    const activeWeekdays = useMemo(
        () =>
            new Set(
                windows
                    .filter((window) => window.is_active)
                    .map((window) => window.day_of_week)
            ),
        [windows]
    );

    const dayHasAvailability =
        activeWeekdays.size === 0
            ? false
            : activeWeekdays.has(weekdayOfDateKey(date));

    /**
     * Seed the date once per opening, then leave it to the therapist.
     *
     * This used to re-run whenever `windows` changed identity, and React Query
     * hands back an equal-but-new array on every refetch — including the
     * refetch triggered by the window regaining focus, which is exactly what
     * happens when the native date picker closes. The seed then overwrote the
     * day just chosen, so the field looked unselectable.
     */
    const seededFor = useRef<string | null>(null);

    useEffect(() => {
        if (!open) {
            seededFor.current = null;
            setTitle("");
            setTitleEdited(false);
            return;
        }
        if (seededFor.current === initialDate) return;
        // Landing on a day with no bookable time makes the empty slot list read
        // as a fault. `nextDateWithAvailability` also skips today once the
        // day's windows have closed, which a weekday check alone would miss.
        const suggested = nextDateWithAvailability(
            new Date(),
            windows,
            settings.timezone
        );
        seededFor.current = initialDate;
        setDate(
            activeWeekdays.has(weekdayOfDateKey(initialDate)) &&
                initialDate >= suggested
                ? initialDate
                : suggested
        );
        setSelectedSlot(null);
    }, [open, initialDate, windows, activeWeekdays, settings.timezone]);

    // A slot chosen for one date, or for a session of another length, must
    // not survive the change.
    useEffect(() => {
        setSelectedSlot(null);
    }, [date, sessionType]);

    /**
     * Preselect the time the week grid was clicked on, once its slots arrive.
     *
     * Only for the day that was clicked, and only once per opening: after that
     * the grid belongs to the therapist, so a refetch must not drag the
     * selection back to where they first pointed.
     */
    const preselectedFor = useRef<string | null>(null);
    const slots = slotsQuery.data?.slots;

    useEffect(() => {
        if (!open || initialMinute === null) {
            preselectedFor.current = null;
            return;
        }
        // Once per kind of session too: a group's slots are not an individual's.
        const attempt = `${initialDate}:${initialMinute}:${sessionType}`;
        if (preselectedFor.current === attempt) return;
        if (date !== initialDate || !slots?.length) return;

        preselectedFor.current = attempt;
        const match = slotMatchingMinute(
            slots,
            slotsQuery.data?.timezone ?? settings.timezone,
            initialMinute
        );
        if (match) setSelectedSlot(match);
    }, [
        open,
        initialMinute,
        initialDate,
        date,
        sessionType,
        slots,
        slotsQuery.data?.timezone,
        settings.timezone,
    ]);

    const defaultTitle = bookingSubject
        ? t("scheduler.schedule.defaultTitle", { name: bookingSubject.label })
        : "";
    // Keyed on `open` too: closing clears the name, and reopening for the same
    // subject leaves `defaultTitle` unchanged, so without it the field would
    // come back empty and Confirm disabled.
    useEffect(() => {
        if (open && !titleEdited) setTitle(defaultTitle);
    }, [open, defaultTitle, titleEdited]);

    const anyPlatformConnected =
        settings.google_connected ||
        settings.zoom_connected ||
        settings.microsoft_connected;
    const locationMissing =
        ubication === SessionUbication.IN_PERSON
            ? !spaceId
            // Only enforced when a platform is available. With none connected
            // the therapist can still book and share a link their own way.
            : anyPlatformConnected && !platform;
    const canSubmit =
        Boolean(bookingSubject) &&
        Boolean(selectedSlot) &&
        !locationMissing &&
        title.trim().length > 0;

    return (
        // The shared Modal, not a hand-rolled overlay: Escape, the backdrop,
        // the focus trap and focus restore all come with it, and the tool
        // stops having two dialogs that dismiss differently.
        <Modal
            isOpen={open}
            onClose={onClose}
            showCloseButton={false}
            ariaLabel={t("scheduler.schedule.newSession")}
            // Its first control is the patient search: focused on a touch
            // screen, the keyboard would cover half the form before the
            // therapist has chosen to type anything.
            initialFocus={isTouchScreen() ? "dialog" : "first-control"}
            className="max-w-lg overflow-hidden rounded-3xl"
        >
            {/* Fields at 16px on a phone: any smaller and iOS zooms the page
                on focus, leaving the therapist to pinch back out. */}
            <div className="flex max-h-[90vh] w-full flex-col gap-4 overflow-y-auto p-5 max-sm:[&_input]:text-base max-sm:[&_select]:text-base sm:p-6">
                <h3 className="text-lg font-semibold">
                    {t("scheduler.schedule.newSession")}
                </h3>

                {subject ? (
                    <div className="flex flex-col gap-1 text-sm">
                        <span className="font-medium">
                            {t("scheduler.schedule.patient")}
                        </span>
                        {/* Named, not silent: the therapist confirms who this
                            commits before choosing a time. */}
                        <p className="rounded-xl border border-gray-200 px-3 py-2.5 font-medium dark:border-gray-700">
                            {subject.label}
                        </p>
                    </div>
                ) : (
                    <PatientFinder
                        patients={patients}
                        selectedId={patientId || null}
                        onSelect={(id) => setPatientId(id ?? "")}
                        disabled={isSaving}
                    />
                )}

                <label className="flex flex-col gap-1 text-sm">
                    {t("scheduler.schedule.title")}
                    <input
                        type="text"
                        aria-label={t("scheduler.schedule.title")}
                        value={title}
                        maxLength={200}
                        placeholder={t("scheduler.schedule.titlePlaceholder")}
                        onChange={(event) => {
                            setTitle(event.target.value);
                            setTitleEdited(true);
                        }}
                        disabled={isSaving}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                    {bookingSubject && !title.trim() && (
                        <span className="text-xs text-red-600 dark:text-red-400">
                            {t("scheduler.schedule.titleRequired")}
                        </span>
                    )}
                </label>

                <label className="flex flex-col gap-1 text-sm">
                    {t("scheduler.schedule.date")}
                    <input
                        type="date"
                        aria-label={t("scheduler.schedule.date")}
                        // A past day is never bookable, and an empty slot list
                        // reads as a fault rather than as "that has passed".
                        min={toDateKey(new Date(), settings.timezone)}
                        value={date}
                        onChange={(event) => setDate(event.target.value)}
                        // Clicking anywhere in the field opens the calendar.
                        // By default only the small icon does, which leaves
                        // the therapist typing the date out by hand.
                        onClick={openDatePicker}
                        onFocus={openDatePicker}
                        className="w-full cursor-pointer rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                </label>

                <div className="flex flex-col gap-1 text-sm">
                    <span>{t("scheduler.schedule.time")}</span>
                    <SlotGrid
                        slots={slotsQuery.data?.slots ?? []}
                        timezone={slotsQuery.data?.timezone ?? settings.timezone}
                        selected={selectedSlot}
                        onSelect={setSelectedSlot}
                        isLoading={slotsQuery.isLoading || availabilityQuery.isLoading}
                        isError={slotsQuery.isError}
                        onRetry={() => slotsQuery.refetch()}
                        disabled={isSaving}
                        dayHasAvailability={dayHasAvailability}
                        hasAnyAvailability={activeWeekdays.size > 0}
                    />
                </div>

                <SessionTypeSelector
                    ubication={ubication}
                    onUbicationChange={setUbication}
                    platform={platform}
                    onPlatformChange={setPlatform}
                    spaceId={spaceId}
                    onSpaceChange={setSpaceId}
                    settings={settings}
                    spaces={spaces}
                    alchelyMeetingAvailable={alchelyMeetingAvailable}
                />

                {courses.length > 0 && (
                    <MentoringModuleFields
                        courses={courses}
                        courseId={courseId}
                        moduleId={moduleId}
                        onCourseChange={(next) => {
                            setCourseId(next);
                            setModuleId("");
                        }}
                        onModuleChange={setModuleId}
                        disabled={isSaving}
                    />
                )}

                <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <Checkbox
                        id="schedule-open-session"
                        checked={openSession}
                        onChange={setOpenSession}
                        disabled={isSaving}
                        label={t("scheduler.schedule.openSession")}
                    />
                    <p className="mt-1 pl-8 text-xs text-gray-500 dark:text-gray-400">
                        {t("scheduler.schedule.openSessionHint")}
                    </p>
                </div>

                {errorMessage && (
                    <p role="alert" className="text-sm text-red-600">
                        {errorMessage}
                    </p>
                )}

                {/* On a phone the form is longer than the screen, and the
                    dialog has no close button: the way out and the way
                    forward stay pinned to the bottom, within a thumb's reach. */}
                <div className="sticky -bottom-5 -mx-5 -mb-5 flex gap-2 border-t border-gray-100 bg-white px-5 py-3 dark:border-white/10 dark:bg-gray-900 sm:static sm:m-0 sm:justify-end sm:border-0 sm:bg-transparent sm:p-0 sm:dark:bg-transparent">
                    <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 text-sm dark:border-white/10 sm:flex-none sm:border-0 sm:py-2">
                        {t("scheduler.common.cancel")}
                    </button>
                    <button
                        type="button"
                        disabled={!canSubmit || isSaving}
                        onClick={() =>
                            onSubmit({
                                ...subjectPayload(bookingSubject as ScheduleSubject),
                                title: title.trim(),
                                start: selectedSlot as string,
                                ubication,
                                online_platform:
                                    ubication === SessionUbication.ONLINE ? platform : null,
                                space_id:
                                    ubication === SessionUbication.IN_PERSON ? spaceId : null,
                                open_session: openSession,
                                course_module_id: moduleId || null,
                            })
                        }
                        className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50 sm:flex-none sm:py-2"
                    >
                        {isSaving
                            ? t("scheduler.schedule.scheduling")
                            : t("scheduler.schedule.confirm")}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
